import { useState, useRef, useCallback, FormEvent } from 'react';
import { useAuth } from '@/auth/auth-provider';
import { getHostName } from '@/tools/office-tools-registry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, AlignLeftIcon } from 'lucide-react';
import { ChatInterface } from './ChatInterface';
import { SettingsPanel } from '@/components/settings/SettingsPanel';
import { ConnectorList } from '@/components/connectors/ConnectorList';
import { AgentSelectionPage } from '@/components/agents/AgentSelectionPage';
import { AgentDialog } from '@/components/agents/AgentDialog';
import { SkillList } from '@/components/skills/SkillList';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { useWorkspace } from '@/lib/use-workspace';
import { useAgents } from '@/lib/use-agents';
import { useDefaultAgent } from '@/lib/use-default-agent';
import { useSkills } from '@/lib/use-skills';
import type { Agent } from '@/lib/agent-types';

type View = 'agent-selection' | 'agent-editor' | 'chat' | 'skills' | 'settings' | 'connectors';

export function TaskPane() {
  const { user, signInWithPassword, signInWithSSO, signOut, isAuthenticated, loading: authLoading, error: authError } = useAuth();

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header />
        <LoginForm
          onSignInWithPassword={signInWithPassword}
          onSignInWithSSO={signInWithSSO}
          loading={authLoading}
          error={authError}
        />
      </div>
    );
  }

  return <AuthenticatedApp user={user} onSignOut={signOut} />;
}

function AuthenticatedApp({
  user,
  onSignOut,
}: {
  user: { name: string; email: string; avatar?: string } | null;
  onSignOut: () => Promise<void>;
}) {
  const { workspace, loading: wsLoading, error: wsError } = useWorkspace();
  const workspaceId = workspace?.id ?? null;
  const { defaultAgent, loading: defaultAgentLoading, error: defaultAgentError, retry: retryDefaultAgent } = useDefaultAgent(workspaceId);

  const [view, setView] = useState<View>('chat');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [dialogAgent, setDialogAgent] = useState<Agent | null | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { createAgent, updateAgent } = useAgents(workspaceId, false);
  const cancelChatRef = useRef<(() => void | Promise<void>) | null>(null);

  const handleRegisterCancel = useCallback((cancelFn: () => void) => {
    cancelChatRef.current = cancelFn;
  }, []);

  // Use the default agent as selectedAgent when none is explicitly chosen
  const effectiveAgent = selectedAgent ?? defaultAgent;

  // Show loading while workspace and default agent are being resolved
  if (wsLoading || defaultAgentLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header user={user} />
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <Loader2 className="size-6 text-primary animate-spin" />
          <p className="text-sm text-muted-foreground">Setting up {getHostName()} workspace...</p>
        </div>
      </div>
    );
  }

  if (wsError) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header user={user} />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <p className="text-sm text-destructive text-center">{wsError}</p>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (defaultAgentError) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <Header user={user} />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <p className="text-sm text-destructive text-center">{defaultAgentError}</p>
          <Button variant="outline" size="sm" onClick={retryDefaultAgent}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const handleAgentSelect = (agent: Agent) => {
    setSelectedAgent(agent);
    setSessionId(null);
    setView('chat');
  };

  const handleSessionSelect = (sid: string, _agentId?: string | null) => {
    setSessionId(sid);
    setView('chat');
  };

  const handleGlobalNewChat = () => {
    setSessionId(null);
    setView('chat');
  };

  const handleNavigate = (nextView: View) => {
    if (nextView === 'agent-selection') {
      cancelChatRef.current?.();
      cancelChatRef.current = null;
      setSelectedAgent(null);
      setSessionId(null);
    }
    setView(nextView);
  };


  const handleCreateAgent = () => {
    setDialogAgent(null);
    setView('agent-editor');
  };

  const handleEditAgent = (agent: Agent) => {
    setDialogAgent(agent);
    setView('agent-editor');
  };

  const handleSaveAgent = async (data: any) => {
    if (dialogAgent === null) {
      return await createAgent(data);
    } else if (dialogAgent) {
      return await updateAgent(dialogAgent.id, data);
    }
    throw new Error('Invalid dialog state');
  };


  return (
    <div className="flex flex-col h-screen bg-background relative overflow-hidden">
      {/* Gradient overlay from top fading smoothly downward */}
      <div className="absolute top-0 left-0 right-0 h-1/3 pointer-events-none z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.12] via-primary/[0.08] via-15% via-primary/[0.04] via-transparent via-transparent via-transparent to-transparent" />
      </div>
      
      <AppSidebar
        open={sidebarOpen}
        currentView={view}
        currentSessionId={sessionId}
        workspaceId={workspaceId}
        onClose={() => setSidebarOpen(false)}
        onNavigate={(nextView) => {
          handleNavigate(nextView);
          setSidebarOpen(false);
        }}
        onSessionSelect={(sid) => {
          handleSessionSelect(sid);
          setSidebarOpen(false);
        }}
        onNewChat={() => {
          handleGlobalNewChat();
          setSidebarOpen(false);
        }}
        onSignOut={onSignOut}
      />

      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/20"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
        />
      )}
      <Header
        user={user}
        onToggleSidebar={() => setSidebarOpen(true)}
      />
      {view === 'agent-selection' && (
        <AgentSelectionPage
          key={refreshKey}
          workspaceId={workspaceId}
          onAgentSelect={handleAgentSelect}
          onSessionSelect={handleSessionSelect}
          onCreateAgent={handleCreateAgent}
          onEditAgent={handleEditAgent}
        />
      )}
      {view === 'chat' && (
        <ChatInterface
          selectedAgentId={effectiveAgent?.id}
          initialSessionId={sessionId}
          workspaceId={workspaceId}
          onRegisterCancel={handleRegisterCancel}
          onSessionCreated={setSessionId}
        />
      )}
      {view === 'skills' && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <SkillList workspaceId={workspaceId} />
        </div>
      )}
      {view === 'settings' && (
        <div className="flex-1 min-h-0">
          <SettingsPanel workspaceId={workspaceId} />
        </div>
      )}
      {view === 'connectors' && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ConnectorList workspaceId={workspaceId} />
        </div>
      )}
      {view === 'agent-editor' && dialogAgent !== undefined && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <AgentDialog
            agent={dialogAgent}
            workspaceId={workspaceId}
            variant="panel"
            onClose={() => {
              setDialogAgent(undefined);
              setRefreshKey((prev) => prev + 1);
              setView('agent-selection');
            }}
            onSave={handleSaveAgent}
          />
        </div>
      )}
    </div>
  );
}

function Header({
  user,
  onToggleSidebar,
}: {
  user?: { name: string; email: string; avatar?: string } | null;
  onToggleSidebar?: () => void;
}) {
  return (
    <div className="relative flex items-center justify-between px-3 py-2 bg-transparent shrink-0 z-20">
      <div className="flex items-center gap-2">
        <button
          onClick={onToggleSidebar}
          className="hover:bg-accent/50 rounded p-1 transition-colors"
          title="Open menu"
        >
          <AlignLeftIcon className="size-5 text-muted-foreground" strokeWidth={2.2} />
        </button>
      </div>
      {user && (
        <div className="flex items-center gap-1.5 shrink-0">
          <Avatar className="size-6 ring-2 ring-primary/10">
            {user.avatar && <AvatarImage src={user.avatar} />}
            <AvatarFallback className="text-[10px]">
              {user.name?.charAt(0).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </div>
      )}
    </div>
  );
}

function LoginForm({
  onSignInWithPassword,
  onSignInWithSSO,
  loading,
  error,
}: {
  onSignInWithPassword: (email: string, password: string) => Promise<void>;
  onSignInWithSSO: () => Promise<void>;
  loading: boolean;
  error: string | null;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [ssoLoading, setSsoLoading] = useState(false);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!email || !password) {
      setLocalError('Please enter email and password');
      return;
    }
    try {
      await onSignInWithPassword(email, password);
    } catch {
      // error is set by auth provider
    }
  };

  const handleSSO = async () => {
    setLocalError(null);
    setSsoLoading(true);
    try {
      await onSignInWithSSO();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'SSO sign in failed';
      if (message !== 'Sign in cancelled') {
        setLocalError(message);
      }
    } finally {
      setSsoLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4">
      <div className="text-center">
        <h2 className="text-base font-semibold">Welcome to AI Studio</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Sign in to start using AI assistants with {getHostName()}
        </p>
      </div>

      <div className="w-full max-w-xs flex flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={loading || ssoLoading}
          className="w-full"
          onClick={handleSSO}
        >
          {ssoLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
          Sign in with Microsoft SSO
        </Button>

        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-[11px] text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-3">
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
          />

          {displayError && (
            <p className="text-xs text-destructive text-center">{displayError}</p>
          )}

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            Sign In with Email
          </Button>
        </form>
      </div>
    </div>
  );
}
